import { nextTick } from 'vue'
import { ZMessage } from '@zoryn/components/message'
import { ZMessageBox } from '@zoryn/components/message-box'
import {
  activateRowEdit,
  hasActiveRowEdit,
  ROW_EDIT_BLOCKED_MESSAGE,
} from '@zoryn/components/grid/src/methods/edit'
import { resolveGridEditable } from '@zoryn/components/grid/src/column/meta'
import { getRowId } from '@zoryn/components/grid/src/utils/row-id'
import { notifyUnhandledRequestError } from '@zoryn/components/grid/src/utils/notify-request-failure'
import { resolveGridRequest } from '@zoryn/components/grid/src/utils/resolve-grid-request'
import { getGridTreeOptions, reloadTreeData } from './query'
import {
  hasPersistedRowId,
  isNewGridRow,
  prepareRowForInsert,
  prepareRowForUpdate,
  resolveModuleUrls,
} from '../api/api-convention'
import { resolveTreeConfig } from '../tree/tree-config'
import { syncTreeTableData } from '../tree/sync-table-data'
import { hasTreeChildren } from '../tree/flatten-visible'
import type { DataGridTree } from '../types'
import type { HttpRequest } from '@zoryn/network'

function appendParentIdToRow(
  row: Record<string, unknown>,
  parentId: unknown,
  $grid: DataGridTree
) {
  const { parentField } = resolveTreeConfig($grid.options)
  row[parentField] = parentId
}

function collectRowsToSave($grid: DataGridTree) {
  const { reactData } = $grid
  const { actived } = reactData.editStore
  if (reactData.editStr === 'new') {
    const newRows = $grid.internalData.flatSource.filter(
      (row) => !hasPersistedRowId(row)
    )
    if (newRows.length) {
      return newRows
    }
  }
  if (actived.row) {
    return [actived.row]
  }
  if (reactData.currentRow) {
    return [reactData.currentRow]
  }
  return null
}

async function saveRowsWithConvention(
  request: HttpRequest,
  module: string,
  rows: Record<string, unknown>[],
  options: ReturnType<typeof getGridTreeOptions>
) {
  const urls = resolveModuleUrls(module)
  const insertUrl = options.insertUrl || urls.insertRow
  const batchInsertUrl = options.batchInsertUrl || urls.batchInsert
  const updateUrl = options.updateUrl || urls.updateById

  const insertRows: Record<string, unknown>[] = []
  const updateRows: Record<string, unknown>[] = []

  for (const row of rows) {
    options.beforeUpsert?.(row)
    if (isNewGridRow(row)) {
      options.beforeInsert?.(row)
      insertRows.push(prepareRowForInsert(row))
    } else {
      options.beforeUpdate?.(row)
      updateRows.push(prepareRowForUpdate(row))
    }
  }

  if (insertRows.length === 1) {
    await request.post({ url: insertUrl, params: insertRows[0] })
  } else if (insertRows.length > 1) {
    await request.post({ url: batchInsertUrl, params: insertRows })
  }
  for (const row of updateRows) {
    await request.post({ url: updateUrl, params: row })
  }
}

export function useToolbar($grid: DataGridTree) {
  const { reactData, internalData } = $grid
  const treeConfig = () => resolveTreeConfig($grid.options)

  const insertRow = async (parentId: unknown) => {
    if (!resolveGridEditable($grid.props, $grid.options)) {
      return
    }
    if (hasActiveRowEdit($grid as never)) {
      ZMessage.warning(ROW_EDIT_BLOCKED_MESSAGE)
      return
    }
    const options = getGridTreeOptions($grid)
    const record = { ...(options.defaultRow || {}) } as Record<string, unknown>
    appendParentIdToRow(record, parentId, $grid)
    getRowId(record)
    reactData.editStr = 'new'
    internalData.flatSource.push(record)
    if (parentId !== treeConfig().rootParentValue) {
      internalData.expandSet.add(String(parentId))
    }
    syncTreeTableData($grid)
    $grid.setCurrentRow(record)
    activateRowEdit($grid as never, record)
    await nextTick()
    await $grid.scrollToTop?.()
    await nextTick()
    await $grid.validateRows([record], { throwError: false })
    await $grid.focusRowFirstInput(record)
  }

  $grid.handleInsert = () => insertRow(treeConfig().rootParentValue)

  $grid.handleInsertChild = async () => {
    const row = reactData.currentRow
    if (!row) {
      ZMessage.warning('请先选中父节点')
      return
    }
    const config = treeConfig()
    await insertRow(row[config.rowField])
  }

  const saveData = async (andNew = false) => {
    const options = getGridTreeOptions($grid)
    const rows = collectRowsToSave($grid)
    if (!rows?.length) {
      ZMessage.warning('无可保存的数据')
      return
    }
    try {
      await $grid.validateRows(rows)
    } catch (e: unknown) {
      ZMessage.warning((e as Error)?.message ?? '请完善必填项')
      return
    }
    try {
      reactData.loading = true
      const request = resolveGridRequest($grid)
      await saveRowsWithConvention(request, options.module, rows, options)
    } catch (e: unknown) {
      notifyUnhandledRequestError(e, '保存失败')
      throw e
    } finally {
      reactData.loading = false
    }
    try {
      reactData.editStr = ''
      reactData.editStore.actived = { row: null, field: null }
      reactData.validErrorMaps = {}
      await reloadTreeData($grid)
      ZMessage.success('保存成功')
      if (andNew) {
        await $grid.handleInsert?.()
      }
    } catch {
      ZMessage.success('保存成功')
      ZMessage.warning('列表刷新失败，请手动刷新')
    }
  }

  $grid.saveData = () => saveData(false)
  $grid.saveDataAndNew = () => saveData(true)

  $grid.handleDeleteRows = async () => {
    const options = getGridTreeOptions($grid)
    const row = reactData.currentRow ?? reactData.editStore.actived.row
    if (!row) {
      ZMessage.warning('请先选中要删除的数据')
      return
    }
    const config = treeConfig()
    if (
      hasTreeChildren(row, internalData.flatSource, config)
      && hasPersistedRowId(row)
    ) {
      ZMessage.warning('请先删除子节点')
      return
    }
    try {
      await ZMessageBox.confirm('确定删除当前选中的记录吗?', '提示', {
        confirmButtonText: '确认',
        cancelButtonText: '取消',
        type: 'warning',
      })
    } catch {
      return
    }
    if (!hasPersistedRowId(row)) {
      internalData.flatSource = internalData.flatSource.filter((item) => item !== row)
      syncTreeTableData($grid)
      reactData.editStr = ''
      $grid.setCurrentRow(null)
      ZMessage.success('删除成功')
      return
    }
    try {
      reactData.loading = true
      options.beforeDelete?.(row)
      const urls = resolveModuleUrls(options.module)
      const request = resolveGridRequest($grid)
      await request.post({
        url: options.deleteByIdUrl || urls.deleteById,
        params: { id: row.id },
      })
    } catch (e: unknown) {
      notifyUnhandledRequestError(e, '删除失败')
      return
    } finally {
      reactData.loading = false
    }
    try {
      reactData.editStr = ''
      await reloadTreeData($grid)
      ZMessage.success('删除成功')
    } catch {
      ZMessage.success('删除成功')
      ZMessage.warning('列表刷新失败，请手动刷新')
    }
  }
}
